/**
 * Hook useGlobalSearch
 * Recherche globale à travers toutes les entités du CRM
 */

import { useState, useEffect, useCallback } from 'react';
import { useSupabase } from './useSupabase';
import { useCanAccessUsers } from './useCanAccessUsers';
import { useUsers } from '../contexts/UsersContext';
import { ROUTES } from '@/lib/routes';
import { CONTACT_STATUS_LABELS, CONTACT_STATUS_COLORS } from '@/types/contact';
import type { ContactStatus } from '@/types/contact';
import { CREW_STATUS_LABELS, CREW_STATUS_COLORS } from '@/types/crew';
import type { CrewStatus } from '@/types/crew';
import { buildSearchOrConditions, escapeLikeMethod } from '@/lib/postgrestSearch';

// =====================================================
// TYPES
// =====================================================

export type SearchResultType =
  | 'contact'
  | 'crew'
  | 'prospect'
  | 'product'
  | 'vehicle'
  | 'school'
  | 'association'
  | 'form'
  | 'user';

export interface SearchResultStatus {
  label: string;
  colors: { bg: string; text: string; border: string };
}

function resolveStatus<T extends string>(
  status: T,
  labels: Record<T, string>,
  colors: Record<T, { bg: string; text: string; border: string }>
): SearchResultStatus | undefined {
  return labels[status] ? { label: labels[status], colors: colors[status] } : undefined;
}

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  metadata?: string;
  status?: SearchResultStatus;
  url: string;
}

export interface SearchResultsByType {
  contacts: SearchResult[];
  crews: SearchResult[];
  prospects: SearchResult[];
  products: SearchResult[];
  vehicles: SearchResult[];
  schools: SearchResult[];
  associations: SearchResult[];
  forms: SearchResult[];
  users: SearchResult[];
}

export interface UseGlobalSearchReturn {
  results: SearchResultsByType;
  loading: boolean;
  error: string | null;
  hasResults: boolean;
  totalCount: number;
}

// =====================================================
// LABELS DES TYPES D'ENTITÉS
// =====================================================

export const ENTITY_TYPE_LABELS: Record<SearchResultType, string> = {
  contact: 'Contacts',
  crew: 'Équipages',
  prospect: 'Prospects',
  product: 'Produits',
  vehicle: 'Véhicules',
  school: 'Écoles',
  association: 'Associations',
  form: 'Formulaires',
  user: 'Utilisateurs',
};

// =====================================================
// HOOK
// =====================================================

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const RESULTS_PER_TYPE = 5;

export function useGlobalSearch(query: string): UseGlobalSearchReturn {
  const supabase = useSupabase();
  const canAccessUsers = useCanAccessUsers();
  const { users } = useUsers(); // ✅ Cache des utilisateurs

  const [results, setResults] = useState<SearchResultsByType>({
    contacts: [],
    crews: [],
    prospects: [],
    products: [],
    vehicles: [],
    schools: [],
    associations: [],
    forms: [],
    users: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fonction de recherche pour chaque type d'entité
  const searchContacts = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        const { data, error: searchError } = await supabase
          .from('contacts')
          .select('id, first_name, last_name, email, status')
          .or(buildSearchOrConditions(['first_name', 'last_name', 'email'], searchQuery))
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((c) => ({
            id: c.id,
            type: 'contact' as const,
            title: `${c.first_name} ${c.last_name}`,
            subtitle: c.email,
            status: resolveStatus(
              c.status as ContactStatus,
              CONTACT_STATUS_LABELS,
              CONTACT_STATUS_COLORS
            ),
            url: ROUTES.DATA.CONTACTS.DETAIL(c.id),
          })) || []
        );
      } catch (err) {
        console.error('Error searching contacts:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchCrews = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        // R10 : recherche sur nom équipage OU numéro équipage OU code BPI
        // (bpi_code agrégé via form_submissions dans crews_filter_view migration 00046)
        const { data, error: searchError } = await supabase
          .from('crews_filter_view')
          .select('id, name, crew_number, status, bpi_code')
          .or(buildSearchOrConditions(['name', 'crew_number', 'bpi_code'], searchQuery))
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((c) => ({
            id: c.id,
            type: 'crew' as const,
            title: c.name,
            subtitle: c.crew_number || c.bpi_code || undefined,
            status: resolveStatus(c.status as CrewStatus, CREW_STATUS_LABELS, CREW_STATUS_COLORS),
            url: ROUTES.DATA.CREWS.DETAIL(c.id),
          })) || []
        );
      } catch (err) {
        console.error('Error searching crews:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchProspects = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        // Recherche sur nom/prénom/email OU numéro BPI.
        // prospects_view expose les champs contact_* depuis les migrations BPI.
        const { data, error: searchError } = await supabase
          .from('prospects_view')
          .select('id, contact_first_name, contact_last_name, contact_email, bpi_code')
          .or(
            buildSearchOrConditions(
              ['contact_first_name', 'contact_last_name', 'contact_email', 'bpi_code'],
              searchQuery
            )
          )
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((p) => ({
            id: p.id,
            type: 'prospect' as const,
            title:
              [p.contact_first_name, p.contact_last_name].filter(Boolean).join(' ') || 'Prospect',
            subtitle: p.contact_email || undefined,
            metadata: p.bpi_code || undefined,
            url: `${ROUTES.DATA.PROSPECTS}?viewId=${p.id}`,
          })) || []
        );
      } catch (err) {
        console.error('Error searching prospects:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchProducts = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        const { data, error: searchError } = await supabase
          .from('products')
          .select('id, name, start_date, end_date')
          .ilike('name', `%${escapeLikeMethod(searchQuery)}%`)
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((p) => ({
            id: p.id,
            type: 'product' as const,
            title: p.name,
            subtitle: p.start_date
              ? `Du ${new Date(p.start_date).toLocaleDateString('fr-FR')}`
              : undefined,
            url: `${ROUTES.DATA.PRODUCTS}?viewId=${p.id}`,
          })) || []
        );
      } catch (err) {
        console.error('Error searching products:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchVehicles = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        const { data, error: searchError } = await supabase
          .from('vehicles')
          .select('id, registration, owner')
          .or(buildSearchOrConditions(['registration', 'owner'], searchQuery))
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((v) => ({
            id: v.id,
            type: 'vehicle' as const,
            title: v.registration,
            subtitle: v.owner || undefined,
            url: `${ROUTES.DATA.VEHICLES}?viewId=${v.id}`,
          })) || []
        );
      } catch (err) {
        console.error('Error searching vehicles:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchSchools = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        const { data, error: searchError } = await supabase
          .from('schools')
          .select('id, name, city, country')
          .ilike('name', `%${escapeLikeMethod(searchQuery)}%`)
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((s) => ({
            id: s.id,
            type: 'school' as const,
            title: s.name,
            subtitle: s.city || undefined,
            url: `${ROUTES.DATA.SCHOOLS}?viewId=${s.id}`,
          })) || []
        );
      } catch (err) {
        console.error('Error searching schools:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchAssociations = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        const { data, error: searchError } = await supabase
          .from('associations')
          .select('id, name')
          .ilike('name', `%${escapeLikeMethod(searchQuery)}%`)
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((a) => ({
            id: a.id,
            type: 'association' as const,
            title: a.name,
            url: `${ROUTES.DATA.ASSOCIATIONS}?viewId=${a.id}`,
          })) || []
        );
      } catch (err) {
        console.error('Error searching associations:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchForms = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      try {
        const { data, error: searchError } = await supabase
          .from('forms')
          .select('id, name, is_active')
          .ilike('name', `%${escapeLikeMethod(searchQuery)}%`)
          .limit(RESULTS_PER_TYPE);

        if (searchError) throw searchError;

        return (
          data?.map((f) => ({
            id: f.id,
            type: 'form' as const,
            title: f.name,
            metadata: f.is_active ? 'Actif' : 'Inactif',
            url: `${ROUTES.DATA.FORMS.LIST}?viewId=${f.id}`,
          })) || []
        );
      } catch (err) {
        console.error('Error searching forms:', err);
        return [];
      }
    },
    [supabase]
  );

  const searchUsers = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      // Si pas de permissions, retourner vide
      if (!canAccessUsers) {
        return [];
      }

      // Utiliser le cache du UsersContext au lieu d'appeler get_users_list
      // Filtrer côté client
      const filtered = users?.filter((u: any) => {
        const fullName = u.full_name?.toLowerCase() || '';
        const email = u.email?.toLowerCase() || '';
        const q = searchQuery.toLowerCase();
        return fullName.includes(q) || email.includes(q);
      });

      return (
        filtered?.slice(0, RESULTS_PER_TYPE).map((u: any) => ({
          id: u.id,
          type: 'user' as const,
          title: u.full_name || u.email,
          subtitle: u.full_name ? u.email : undefined,
          url: `${ROUTES.ADMIN.USERS}?viewId=${u.id}`,
        })) || []
      );
    },
    [users, canAccessUsers]
  );

  // Fonction principale de recherche
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < MIN_QUERY_LENGTH) {
        setResults({
          contacts: [],
          crews: [],
          prospects: [],
          products: [],
          vehicles: [],
          schools: [],
          associations: [],
          forms: [],
          users: [],
        });
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Recherche parallèle sur toutes les entités avec Promise.allSettled
        // pour ne pas bloquer si une entité échoue
        const searchPromises = await Promise.allSettled([
          searchContacts(searchQuery),
          searchCrews(searchQuery),
          searchProspects(searchQuery),
          searchProducts(searchQuery),
          searchVehicles(searchQuery),
          searchSchools(searchQuery),
          searchAssociations(searchQuery),
          searchForms(searchQuery),
          searchUsers(searchQuery),
        ]);

        // Extraire les résultats (en ignorant les erreurs)
        const [
          contactsResult,
          crewsResult,
          prospectsResult,
          productsResult,
          vehiclesResult,
          schoolsResult,
          associationsResult,
          formsResult,
          usersResult,
        ] = searchPromises;

        setResults({
          contacts: contactsResult.status === 'fulfilled' ? contactsResult.value : [],
          crews: crewsResult.status === 'fulfilled' ? crewsResult.value : [],
          prospects: prospectsResult.status === 'fulfilled' ? prospectsResult.value : [],
          products: productsResult.status === 'fulfilled' ? productsResult.value : [],
          vehicles: vehiclesResult.status === 'fulfilled' ? vehiclesResult.value : [],
          schools: schoolsResult.status === 'fulfilled' ? schoolsResult.value : [],
          associations: associationsResult.status === 'fulfilled' ? associationsResult.value : [],
          forms: formsResult.status === 'fulfilled' ? formsResult.value : [],
          users: usersResult.status === 'fulfilled' ? usersResult.value : [],
        });
      } catch (err) {
        console.error('Global search error:', err);
        setError('Erreur lors de la recherche');
      } finally {
        setLoading(false);
      }
    },
    [
      searchContacts,
      searchCrews,
      searchProspects,
      searchProducts,
      searchVehicles,
      searchSchools,
      searchAssociations,
      searchForms,
      searchUsers,
    ]
  );

  // Effet avec debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Calculer le nombre total de résultats
  const totalCount = Object.values(results).reduce(
    (acc, resultArray) => acc + resultArray.length,
    0
  );

  const hasResults = totalCount > 0;

  return {
    results,
    loading,
    error,
    hasResults,
    totalCount,
  };
}
